import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import api from "../../services/api";
import { validateEmail, pickApiError } from "../../utils/authValidators";

/**
 * ForgotPasswordDialog — Iteration 64
 *
 * Always shows a generic success state, regardless of whether the email
 * exists on file. The backend mirrors this contract to prevent user
 * enumeration. The reset link itself is delivered via email (Resend) or
 * the server console log in dev environments.
 */
export default function ForgotPasswordDialog({
  open,
  onClose,
  defaultEmail = "",
  variant = "default", // "default" | "b2b"
}) {
  const [email, setEmail] = useState(defaultEmail);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const isB2B = variant === "b2b";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    const v = validateEmail(email);
    if (v) {
      setErr(v);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/request", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (e1) {
      // Backend returns 200 even on misses; this is for true network errors.
      setErr(pickApiError(e1, "Could not send reset link. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSent(false);
    setErr("");
    setSubmitting(false);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">
            {sent ? "Check your inbox" : "Reset your password"}
          </DialogTitle>
        </DialogHeader>

        {sent ? (
          <div className="space-y-4 pt-1" data-testid="forgot-password-success">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-slate-700">
                  If an account exists for{" "}
                  <span className="font-semibold">{email}</span>, we've sent a
                  password reset link. It expires in <strong>30 minutes</strong>.
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  Didn't get the email? Check your spam folder, or wait a minute
                  and try again. {isB2B && "Corporate spam filters can delay enterprise emails."}
                </p>
              </div>
            </div>
            <Button
              onClick={handleClose}
              className="w-full bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl h-11"
              data-testid="forgot-password-close-btn"
            >
              Got it
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 pt-1">
            <p className="text-sm text-slate-600">
              Enter the email on your {isB2B ? "B2B" : ""} PickNGo account. We'll
              send a one-time link to set a new password.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="fp-email" className="text-sm font-medium text-slate-700">
                Email address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="fp-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-xl h-11 pl-10 border-slate-200 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20"
                  required
                  data-testid="forgot-password-email-input"
                />
              </div>
              {err && (
                <p className="text-xs text-red-600 mt-1" data-testid="forgot-password-error">
                  {err}
                </p>
              )}
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1 rounded-xl h-11"
                data-testid="forgot-password-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl h-11"
                data-testid="forgot-password-submit-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
